import React, { useState } from 'react';
import { GlassCard } from '../components/GlassCard';
import { Check, Package, Users, FileText, TrendingUp, Calendar, BarChart, ArrowRight } from 'lucide-react';
import { FeatureModal } from '../components/FeatureModal';
import { serviceContent } from '../data/serviceContent';
interface ServicesPageProps {
  isDark: boolean;
}
const services = [{
  id: 'inventory',
  icon: Package,
  title: 'Inventory Management',
  desc: 'Real-time stock tracking, automated reordering, and multi-warehouse support.'
}, {
  id: 'crm',
  icon: Users,
  title: 'Customer Relationship',
  desc: '360° customer view, sales pipeline, and interaction history in one place.'
}, {
  id: 'financial',
  icon: FileText,
  title: 'Financial Management',
  desc: 'Automated accounting, invoicing, expense tracking, and financial reporting.'
}, {
  id: 'sales',
  icon: TrendingUp,
  title: 'Sales & Orders',
  desc: 'Quote-to-cash automation, order processing, and sales performance analytics.'
}, {
  id: 'project',
  icon: Calendar,
  title: 'Project Management',
  desc: 'Task tracking, resource allocation, and project timeline management.'
}, {
  id: 'analytics',
  icon: BarChart,
  title: 'Business Intelligence',
  desc: 'Custom dashboards, KPI tracking, and predictive analytics for decision-making.'
}];
export function ServicesPage({
  isDark
}: ServicesPageProps) {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const getModalContent = (id: string) => {
    const content = serviceContent[id as keyof typeof serviceContent];
    return {
      description: content.description,
      features: content.features,
      benefits: content.benefits
    };
  };
  return <div className="page-transition pt-32 pb-16 px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-344">
      <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-345">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-346">
          Complete Business Management Suite
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-347">
          Everything you need to run your business efficiently, all in one
          integrated platform.
        </p>
      </div>

      {/* Core Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24" data-id="element-348">
        {services.map((service, i) => <GlassCard key={i} hoverEffect className="text-center group" onClick={() => setActiveModal(service.id)} data-id="element-349">
            <div className="w-14 h-14 mx-auto bg-brand-50 dark:bg-brand-900/30 rounded-2xl flex items-center justify-center mb-6 transition-colors group-hover:bg-brand-100 dark:group-hover:bg-brand-800/50" data-id="element-350">
              <service.icon className="w-7 h-7 text-brand-600 dark:text-brand-400" strokeWidth={1.5} data-id="element-351" />
            </div>
            <h3 className="text-xl font-bold mb-3 transition-colors group-hover:text-brand-600 dark:group-hover:text-brand-400" data-id="element-352">
              {service.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-353">
              {service.desc}
            </p>
            <div className="text-brand-600 dark:text-brand-400 text-sm font-medium flex items-center justify-center gap-1 group-hover:gap-2 transition-all" data-id="element-354">
              Learn more <ArrowRight className="w-4 h-4" data-id="element-355" />
            </div>
          </GlassCard>)}
      </div>

      {/* Key Features */}
      <div className="glass-panel rounded-3xl p-12 border border-gray-200 dark:border-gray-700" data-id="element-356">
        <h2 className="text-3xl font-bold text-center mb-12" data-id="element-357">
          Platform Capabilities
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-358">
          {['Multi-currency and multi-language support', 'Role-based access control and permissions', 'Automated workflows and business rules', 'Mobile apps for iOS and Android', 'API integrations with 1000+ tools', 'Cloud-based with 99.99% uptime SLA', 'Real-time collaboration features', 'Customizable reports and dashboards'].map((feature, i) => <div key={i} className="flex items-center gap-3" data-id="element-359">
              <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0" data-id="element-360">
                <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" strokeWidth={3} data-id="element-361" />
              </div>
              <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-362">
                {feature}
              </span>
            </div>)}
        </div>
      </div>

      {/* Service Modals */}
      {services.map(service => <FeatureModal key={service.id} isOpen={activeModal === service.id} onClose={() => setActiveModal(null)} title={service.title} content={getModalContent(service.id)} isDark={isDark} data-id="element-363" />)}
    </div>;
}